import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Formulario, FormularioCampo } from "@/types/formulario";

export function usePublicFormulario(token: string | undefined) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-formulario', token],
    queryFn: async () => {
      if (!token) throw new Error('Token não fornecido');

      // Buscar formulário pelo token público
      const { data: formulario, error: formError } = await supabase
        .from('formularios')
        .select(`
          *,
          clients(id, name)
        `)
        .eq('token_publico', token)
        .eq('publico', true)
        .eq('status', 'ativo')
        .single();

      if (formError) throw formError;
      if (!formulario) throw new Error('Formulário não encontrado ou não está público');

      // Verificar se está expirado
      if (formulario.data_expiracao && new Date(formulario.data_expiracao) < new Date()) {
        throw new Error('Este formulário expirou');
      }

      // Verificar se atingiu o limite de respostas
      if (formulario.limite_respostas && formulario.total_respostas >= formulario.limite_respostas) {
        throw new Error('Este formulário atingiu o limite de respostas');
      }

      // Buscar campos do formulário
      const { data: campos, error: camposError } = await supabase
        .from('formulario_campos')
        .select('*')
        .eq('formulario_id', formulario.id)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      if (camposError) throw camposError;

      return {
        formulario: formulario as Formulario,
        campos: (campos || []) as FormularioCampo[],
      };
    },
    enabled: !!token,
    retry: false,
  });

  return {
    formulario: data?.formulario,
    campos: data?.campos || [],
    isLoading,
    error: error as Error | null,
  };
}
